// src/components/mitchRoom/revenueAnalytics/SeasonView.js
//
// Season (1 Jul – 30 Jun) / calendar-year / next-12-months view of the forecast:
// season-to-date, season-end forecast with band, LY comparisons, forecast
// movement (from the daily snapshots), monthly actual/forecast bars with
// the LY line, the assumptions panel with what-if sliders, and the
// accuracy strip for closed months.
import React, { useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import {
  ComposedChart,
  Bar,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend,
} from 'recharts';
import { Tabs, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Switch } from 'components/ui/switch';
import { Label } from 'components/ui/label';
import { Skeleton } from 'components/ui/skeleton';
import { cn } from 'lib/utils';
import { formatCurrency, formatPeriod, formatAxisCurrency } from 'utils/format';
import AssumptionsPanel, { applyScenario, DEFAULT_SCENARIO } from './AssumptionsPanel';

const LY_COLOR = '#9e9e9e';
const ACTUAL = '#1072CA';
const FORECAST = '#254A73';
const SCENARIO = '#B07AA1';

function Delta({ value }) {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  const good = value >= 0;
  return (
    <span className={cn('text-token-xs font-medium', good ? 'text-success' : 'text-destructive')}>
      {good ? '+' : ''}
      {value.toFixed(1)}%
    </span>
  );
}

function Card({ label, value, sub, delta, accent, large, tooltip }) {
  return (
    <div
      className="rounded-token-large border border-borderGrey border-l-4 bg-white p-3"
      style={{ borderLeftColor: accent }}
      title={tooltip}
    >
      <div className="mb-1 flex items-center gap-1">
        <span className="whitespace-nowrap text-token-sm text-muted-foreground">{label}</span>
        {tooltip && <Info size={11} className="shrink-0 text-muted-foreground" aria-hidden="true" />}
      </div>
      <p className={cn('font-semibold', large ? 'text-token-2xl' : 'text-token-lg')}>{value}</p>
      <div className="mt-0.5 flex items-center gap-2">
        {sub && <span className="text-token-xs text-muted-foreground">{sub}</span>}
        <Delta value={delta} />
      </div>
    </div>
  );
}

// Forecast movement: the season snapshot N days before the latest one.
function snapshotDaysAgo(snapshots, targetMonth, days) {
  const rows = (snapshots || [])
    .filter((s) => s.horizon === 'season' && s.target_month === targetMonth)
    .sort((a, b) => (a.as_of_date < b.as_of_date ? -1 : 1));
  if (rows.length < 2) return null;
  const latest = rows[rows.length - 1];
  const cutoff = new Date(`${latest.as_of_date}T00:00:00`);
  cutoff.setDate(cutoff.getDate() - days);
  const iso = cutoff.toISOString().slice(0, 10);
  const older = rows.filter((r) => r.as_of_date <= iso);
  return older.length ? { then: older[older.length - 1], latest } : null;
}

export default function SeasonView({
  forecast,
  loading,
  horizon,
  onHorizonChange,
  scenario,
  onScenarioChange,
}) {
  const [cumulative, setCumulative] = useState(false);
  const months = useMemo(() => forecast?.months || [], [forecast]);
  const totals = forecast?.totals || {};
  const currentIdx = months.findIndex((m) => m.is_current);

  const chartData = useMemo(() => {
    let cumA = 0;
    let cumF = 0;
    let cumL = 0;
    let cumS = 0;
    return months.map((m, i) => {
      const ahead = currentIdx >= 0 ? Math.max(0, i - currentIdx) : 0;
      const isPast = m.is_closed === 1;
      const actual = m.actual ?? 0;
      const fc = m.forecast ?? actual;
      const scen = scenario && !(scenario.churn === 1 && !scenario.yoy && !scenario.collect)
        ? applyScenario(m, scenario, forecast?.assumptions, ahead)
        : null;
      cumA += isPast ? actual : 0;
      cumF += fc;
      cumL += m.ly_calendar ?? 0;
      cumS += scen ?? fc;
      return {
        month: m.month,
        label: formatPeriod(m.month),
        actual_bar: isPast ? actual : m.is_current ? actual : null,
        forecast_bar: isPast ? null : Math.max(0, fc - (m.is_current ? actual : 0)),
        forecast: fc,
        low: m.low ?? fc,
        high: m.high ?? fc,
        band: isPast ? null : [m.low ?? fc, m.high ?? fc],
        ly: m.ly_calendar ?? 0,
        scenario: scen,
        cum_actual: isPast || m.is_current ? cumA + (m.is_current ? actual : 0) : null,
        cum_forecast: cumF,
        cum_ly: cumL,
        cum_scenario: scen !== null ? cumS : null,
        is_closed: isPast,
        is_current: m.is_current === 1,
      };
    });
  }, [months, currentIdx, scenario, forecast]);

  if (loading && !forecast) {
    return (
      <div className="rounded-token-large border border-borderGrey bg-white p-4">
        <Skeleton className="mb-3 h-6 w-64" />
        <Skeleton className="h-[320px] w-full" />
      </div>
    );
  }
  if (!forecast) return null;

  // Season snapshots are keyed by the season's first month (July). Rows
  // from before Oct 2026 used an Aug–Jul season and are not comparable.
  const seasonKey = forecast.season_key || `${forecast.season_year}-07`;
  const move7 = snapshotDaysAgo(forecast.snapshots, seasonKey, 7);
  const move30 = snapshotDaysAgo(forecast.snapshots, seasonKey, 30);
  const scenarioTotal = chartData.some((d) => d.scenario !== null)
    ? chartData.reduce((a, d) => a + (d.scenario ?? d.forecast), 0)
    : null;
  const lyPct = totals.ly_to_date > 0 ? ((totals.actual_to_date - totals.ly_to_date) / totals.ly_to_date) * 100 : null;
  const fcVsLy = totals.ly_total > 0 ? ((totals.forecast - totals.ly_total) / totals.ly_total) * 100 : null;
  const accuracy = forecast.accuracy || [];

  return (
    <div className="flex flex-col gap-3">
      {!forecast.available && (
        <div className="rounded-token-large border border-dashed border-borderGrey bg-white p-3 text-token-md text-muted-foreground">
          The forecast tables have not been built yet (the pipeline runs hourly). Showing actuals only.
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-5">
        <Card
          label={`${forecast.label} to date`}
          value={formatCurrency(totals.actual_to_date, { whole: true })}
          sub={`vs LY ${formatCurrency(totals.ly_to_date, { whole: true })}`}
          delta={lyPct}
          accent="#59A14F"
          large
          tooltip="Net revenue over the closed months plus the current month to date, compared with the same whole months last year."
        />
        <Card
          label={`${forecast.horizon === 'season' ? 'Season-end' : 'Window'} forecast`}
          value={formatCurrency(totals.forecast, { whole: true })}
          sub={`${formatCurrency(totals.low, { whole: true })} – ${formatCurrency(totals.high, { whole: true })}`}
          delta={fcVsLy}
          accent={FORECAST}
          large
          tooltip="Sum of actuals and monthly forecasts across the window. The band is the low (1.5× churn, no new business) to high (0.5× churn, undamped YoY) scenario."
        />
        <Card
          label="Last year, same window"
          value={formatCurrency(totals.ly_total, { whole: true })}
          sub="calendar months"
          accent={LY_COLOR}
        />
        <Card
          label="Forecast movement"
          value={
            move7
              ? `${move7.latest.forecast - move7.then.forecast >= 0 ? '+' : ''}${formatCurrency(
                  move7.latest.forecast - move7.then.forecast,
                  { whole: true }
                )}`
              : '—'
          }
          sub={
            move7
              ? `vs 7d ago${move30 ? ` · ${move30.latest.forecast - move30.then.forecast >= 0 ? '+' : ''}${formatCurrency(move30.latest.forecast - move30.then.forecast, { whole: true })} vs 30d` : ''}`
              : 'needs a week of snapshots'
          }
          accent="#eda100"
          tooltip="How the season-end forecast has moved since the snapshot 7 / 30 days ago (one snapshot per day is kept)."
        />
        <Card
          label="Your scenario"
          value={scenarioTotal !== null ? formatCurrency(scenarioTotal, { whole: true }) : '—'}
          sub={scenarioTotal !== null ? `${scenarioTotal - totals.forecast >= 0 ? '+' : ''}${formatCurrency(scenarioTotal - totals.forecast, { whole: true })} vs model` : 'adjust the sliders below'}
          accent={SCENARIO}
        />
      </div>

      <div className="rounded-token-large border border-borderGrey bg-white p-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-token-xl font-semibold">Monthly — actual, forecast and last year</h3>
            <p className="text-token-xs text-muted-foreground">
              Solid = collected; hatched = forecast (shaded band = low–high); grey line = same month last year.
              {forecast.forecast_as_of ? ` Forecast built ${String(forecast.forecast_as_of).replace('T', ' ').slice(0, 16)}.` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <Tabs value={horizon} onValueChange={onHorizonChange}>
              <TabsList>
                <TabsTrigger value="season">Season</TabsTrigger>
                <TabsTrigger value="calendar_year">Calendar year</TabsTrigger>
                <TabsTrigger value="next_12m">Next 12 months</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex items-center gap-2">
              <Switch id="season-cumulative" checked={cumulative} onCheckedChange={setCumulative} />
              <Label htmlFor="season-cumulative" className="cursor-pointer text-token-base font-normal">
                Cumulative
              </Label>
            </div>
          </div>
        </div>
        <div className="h-[340px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <defs>
                <pattern id="fc-hatch" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                  <rect width="6" height="6" fill="#ffffff" />
                  <line x1="0" y1="0" x2="0" y2="6" stroke={FORECAST} strokeWidth="2" />
                </pattern>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#DADADA" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} />
              <YAxis tickFormatter={formatAxisCurrency} tick={{ fontSize: 11 }} width={58} />
              <Tooltip
                formatter={(v, n) => {
                  const names = {
                    actual_bar: 'Collected',
                    forecast_bar: 'Forecast (remaining)',
                    ly: 'Last year',
                    band: 'Low – high',
                    scenario: 'Your scenario',
                    cum_actual: 'Collected (cumulative)',
                    cum_forecast: 'Forecast (cumulative)',
                    cum_ly: 'Last year (cumulative)',
                    cum_scenario: 'Your scenario (cumulative)',
                  };
                  if (v === null || v === undefined) return [null, null];
                  if (Array.isArray(v)) return [`${formatCurrency(v[0], { whole: true })} – ${formatCurrency(v[1], { whole: true })}`, names[n] || n];
                  return [formatCurrency(v, { whole: true }), names[n] || n];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {cumulative ? (
                <>
                  <Line type="monotone" dataKey="cum_ly" name="Last year (cumulative)" stroke={LY_COLOR} strokeWidth={2} strokeDasharray="6 4" dot={false} isAnimationActive={false} />
                  <Line type="monotone" dataKey="cum_forecast" name="Forecast (cumulative)" stroke={FORECAST} strokeWidth={2} strokeDasharray="4 3" dot={false} isAnimationActive={false} />
                  <Line type="monotone" dataKey="cum_actual" name="Collected (cumulative)" stroke={ACTUAL} strokeWidth={2.5} dot={false} connectNulls={false} isAnimationActive={false} />
                  {scenarioTotal !== null && (
                    <Line type="monotone" dataKey="cum_scenario" name="Your scenario (cumulative)" stroke={SCENARIO} strokeWidth={2} strokeDasharray="2 3" dot={false} isAnimationActive={false} />
                  )}
                </>
              ) : (
                <>
                  <Area type="monotone" dataKey="band" name="Low – high" stroke="none" fill={FORECAST} fillOpacity={0.12} isAnimationActive={false} connectNulls={false} />
                  <Bar dataKey="actual_bar" name="Collected" stackId="m" fill={ACTUAL} isAnimationActive={false}>
                    {chartData.map((d) => (
                      <Cell key={d.month} fill={d.is_current ? '#7db6e6' : ACTUAL} />
                    ))}
                  </Bar>
                  <Bar dataKey="forecast_bar" name="Forecast (remaining)" stackId="m" fill="url(#fc-hatch)" stroke={FORECAST} isAnimationActive={false} />
                  <Line type="monotone" dataKey="ly" name="Last year" stroke={LY_COLOR} strokeWidth={2} strokeDasharray="6 4" dot={{ r: 2 }} isAnimationActive={false} />
                  {scenarioTotal !== null && (
                    <Line type="monotone" dataKey="scenario" name="Your scenario" stroke={SCENARIO} strokeWidth={2} strokeDasharray="2 3" dot={false} connectNulls isAnimationActive={false} />
                  )}
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <AssumptionsPanel
        assumptions={forecast.assumptions}
        scenario={scenario || DEFAULT_SCENARIO}
        onScenarioChange={onScenarioChange}
      />

      {accuracy.length > 0 && (
        <div className="rounded-token-large border border-borderGrey bg-white p-4">
          <h3 className="text-token-xl font-semibold">Forecast accuracy — closed months</h3>
          <p className="mb-2 text-token-xs text-muted-foreground">
            The forecast for each month as it stood on day 1 / 10 / 20 of that month, against what was
            collected. Snapshots start with the first pipeline run that built the forecast.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-token-md">
              <thead>
                <tr>
                  <th className="border-b border-borderGrey p-2 text-left font-semibold">Month</th>
                  <th className="border-b border-borderGrey p-2 text-right font-semibold">Day 1</th>
                  <th className="border-b border-borderGrey p-2 text-right font-semibold">Day 10</th>
                  <th className="border-b border-borderGrey p-2 text-right font-semibold">Day 20</th>
                  <th className="border-b border-borderGrey p-2 text-right font-semibold">Actual</th>
                  <th className="border-b border-borderGrey p-2 text-right font-semibold">Day-10 error</th>
                </tr>
              </thead>
              <tbody>
                {accuracy.map((a) => {
                  const err = a.day10 !== null && a.day10 !== undefined && a.actual ? ((a.day10 - a.actual) / a.actual) * 100 : null;
                  return (
                    <tr key={a.month}>
                      <td className="border-b border-borderGrey p-2">{formatPeriod(a.month)}</td>
                      {['day1', 'day10', 'day20'].map((k) => (
                        <td key={k} className="border-b border-borderGrey p-2 text-right tabular-nums">
                          {a[k] !== null && a[k] !== undefined ? formatCurrency(a[k], { whole: true }) : '—'}
                        </td>
                      ))}
                      <td className="border-b border-borderGrey p-2 text-right font-medium tabular-nums">
                        {formatCurrency(a.actual, { whole: true })}
                      </td>
                      <td className="border-b border-borderGrey p-2 text-right tabular-nums">
                        {err !== null ? (
                          <span className={cn(Math.abs(err) <= 8 ? 'text-success' : 'text-destructive')}>
                            {err >= 0 ? '+' : ''}
                            {err.toFixed(1)}%
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
